import React, { FC, useState } from 'react';

import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';

import AuthDivider from 'components/Auth/AuthDivider';
import AuthLayout from 'components/Auth/AuthLayout';
import GoogleSignInButton from 'components/Auth/GoogleSignInButton';
import OtpAuthForm, { AuthHandoffState } from 'components/Auth/OtpAuthForm';
import PasswordLoginForm from 'components/Auth/PasswordLoginForm';
import { PASSWORD_LOGIN_ENABLED } from 'utils/featureFlags';

export interface PageLoginProps {
    className?: string;
}

const PageLogin: FC<PageLoginProps> = ({ className = '' }) => {
    // Set when somebody bounced here from /signup because their address already
    // has an account. Saves them typing it a second time.
    const location = useLocation();
    const handoffEmail = (location.state as AuthHandoffState | null)?.email ?? '';

    // Password sign-in is off by default now (PASSWORD_LOGIN_ENABLED), so this
    // whole block is normally absent. It stays for the one case it exists for:
    // flipping the flag back on if the passwordless channels have a bad day.
    const [showPasswordForm, setShowPasswordForm] = useState<boolean>(false);
    // Set when the API says the flag has already been switched off server-side,
    // which can happen mid-session in a build that still ships the form.
    const [passwordDisabledMessage, setPasswordDisabledMessage] = useState<string>('');

    const passwordLoginAvailable = PASSWORD_LOGIN_ENABLED && !passwordDisabledMessage;

    return (
        <div className={`nc-PageLogin ${className}`} data-nc-id="PageLogin">
            <Helmet>
                <title>Login | Instaacard</title>
            </Helmet>
            <AuthLayout
                title="Login"
                subtitle="Sign in to manage your digital identity, profiles and connections."
                footer={
                    <>
                        New user? {` `}
                        <Link
                            className="font-medium text-green-600 hover:underline"
                            to="/signup"
                            // Only worth carrying when we were handed one; an
                            // empty state entry is just noise in the history.
                            state={handoffEmail ? ({ email: handoffEmail } as AuthHandoffState) : undefined}
                        >
                            Create an account
                        </Link>
                    </>
                }
            >
                <GoogleSignInButton text="signin_with" />

                <AuthDivider />

                <OtpAuthForm mode="login" defaultEmail={handoffEmail} />

                {passwordDisabledMessage && (
                    <p className="rounded-2xl bg-neutral-100 px-4 py-3 text-center text-sm text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
                        {passwordDisabledMessage}
                    </p>
                )}

                {passwordLoginAvailable && (
                    <div className="space-y-6">
                        <AuthDivider />

                        {showPasswordForm ? (
                            <PasswordLoginForm
                                onPasswordLoginDisabled={(message) => {
                                    setShowPasswordForm(false);
                                    setPasswordDisabledMessage(message);
                                }}
                            />
                        ) : (
                            <button
                                type="button"
                                className="block w-full text-center text-sm text-green-600 hover:underline"
                                onClick={() => setShowPasswordForm(true)}
                            >
                                Sign in with a password instead
                            </button>
                        )}
                    </div>
                )}

            </AuthLayout>
        </div>
    );
};

export default PageLogin;
